<script lang="ts">
	import type { BrandConfig, ProductConfig } from '$lib/brand/types';
	import ProductCTA from '$lib/components/ProductCTA.svelte';

	interface Props {
		config: BrandConfig;
		className?: string;
	}

	let { config, className = '' }: Props = $props();
</script>

<div class="hero {className}">
	<div class="hero-content">
		<h1 class="hero-title">{config.productName}</h1>
		<p class="hero-description">{config.productDescription}</p>
		<div class="hero-actions">
			<a href="/landing/" class="cta-button primary">Get Started</a>
			<a href="/about/" class="cta-button secondary">Learn More</a>
		</div>

		{#if config.product}
			<div class="product-cta-section">
				<ProductCTA product={config.product} />
			</div>
		{/if}
	</div>
</div>

<style lang="scss">
	@use "$lib/scss/_mixins.scss" as *;
	@use "$lib/scss/_themes.scss" as *;
	.hero {
		background: linear-gradient(135deg, var(--brand-primary), var(--brand-secondary));
		color: white;
		padding: var(--spacing--content-inner);
		text-align: center;
		min-height: 60vh;
		display: flex;
		align-items: center;
		justify-content: center;
		max-width: var(--spacing--page-max-width);
		margin: 1rem auto;
	}

	.hero-content {
		max-width: var(--spacing--page-max-width);
		margin: 0 auto;
	}

	.hero-title {
		font-size: 3.5rem;
		font-weight: 700;
		margin-bottom: 1.5rem;
		line-height: 1.2;
		color: white !important;
	}

	.hero-description {
		font-size: 1.25rem;
		margin-bottom: 2.5rem;
		opacity: 0.9;
		line-height: 1.6;
		color: white !important;
	}

	.hero :global(h1) {
		color: white !important;
	}

	.hero :global(h2) {
		color: white !important;
	}

	.hero :global(h3) {
		color: white !important;
	}

	.hero :global(p) {
		color: white !important;
	}

	.hero :global(a) {
		color: white !important;
	}

	/* Override button text colors to ensure proper contrast */
	.hero :global(.cta-button.primary) {
		color: var(--brand-primary) !important;
	}

	.hero :global(.cta-button.secondary) {
		color: white !important;
	}

	.hero :global(.cta-button.primary:hover) {
		color: var(--brand-primary) !important;
	}

	.hero :global(.cta-button.secondary:hover) {
		color: var(--brand-primary) !important;
	}

	.hero :global(.buy-button) {
		color: var(--brand-primary) !important;
	}

	.hero :global(.buy-button:hover) {
		color: var(--brand-primary) !important;
	}

	.hero-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
	}

	.product-cta-section {
		margin-top: 2rem;
	}

	.cta-button {
		display: inline-block;
		padding: 1rem 2rem;
		border-radius: 8px;
		text-decoration: none;
		font-weight: 600;
		font-size: 1.1rem;
		transition: all 0.3s ease;
		border: 2px solid transparent;
		box-sizing: border-box;
	}

	.cta-button.primary {
		background-color: white;
		color: var(--brand-primary);
	}

	.cta-button.primary:hover {
		background-color: rgba(255, 255, 255, 0.9);
		transform: translateY(-2px);
	}

	.cta-button.secondary {
		background-color: transparent;
		color: white;
		border-color: white;
	}

	.cta-button.secondary:hover {
		background-color: white;
		color: var(--brand-primary);
		transform: translateY(-2px);
	}

	@media (max-width: 768px) {
		.hero-title {
			font-size: 2.5rem;
		}

		.hero-description {
			font-size: 1.1rem;
		}

		.hero-actions {
			flex-direction: column;
			align-items: center;
		}

		.cta-button {
			width: 100%;
			max-width: 300px;
		}
	}
</style>